import React, { useState } from 'react';
import { updateProfile } from 'firebase/auth';
import { auth } from '../authentication/firebase-config';
import authService from '../authentication/AuthService';

/**
 * ProfileTab – static summary & recommendations block, plus an editable
 * display-name card.
 *
 * The Risk Assessment was extracted from this tab into its own
 * top-level sidebar entry (RiskAssessmentTab) on 2026-06-17 — the
 * assessment is substantial enough to warrant its own surface and the
 * student mental model treats "my risk profile" as distinct from
 * "my financial profile summary."
 *
 * Display-name editing (added 2026-06-29)
 * ───────────────────────────────────────
 * The app header (Header.js) renders `user?.displayName` straight off the
 * Firebase auth user, which App.js holds in React state and only re-sets
 * from the onAuthStateChanged callback. `updateProfile` mutates the auth
 * user in place but does NOT fire onAuthStateChanged, so the header keeps
 * showing the old name until something re-seeds App's user state.
 *
 * Per scope, this tab must not edit App.js or AuthService.js, so we take
 * the self-contained path: after a successful save we call
 * window.location.reload(). On reload, onAuthStateChanged rehydrates the
 * persisted user (with the new displayName) and the header picks it up.
 * We also write users/{uid}.displayName so the Firestore profile doc stays
 * consistent with the auth record (the header does not read it, but other
 * surfaces / exports may).
 *
 * Props
 *  ─ emergencyFund: { sixMonthGoal: number }
 */
const ProfileTab = ({
  emergencyFund,
}) => {
  const currentUser = authService.getCurrentUser();
  const [name, setName] = useState(currentUser?.displayName || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const initialName = currentUser?.displayName || '';
  const trimmed = name.trim();
  const dirty = trimmed !== initialName.trim();
  const canSave = !saving && dirty && trimmed.length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const user = auth.currentUser;
      if (!user) {
        setError('Your session has expired. Please sign in again.');
        setSaving(false);
        return;
      }

      // 1. Update the Firebase auth displayName — this is what the header reads.
      await updateProfile(user, { displayName: trimmed });

      // 2. Keep the Firestore users/{uid} profile doc in sync (best-effort;
      //    the header does not read it, so a failure here should not block).
      try {
        await authService.updateUserDocument(user.uid, { displayName: trimmed });
      } catch (docErr) {
        console.error('Failed to sync displayName to Firestore profile doc:', docErr);
      }

      // 3. Reload so App's auth-state subscription re-seeds `user` and the
      //    header (Header.js) re-renders with the new name. See the block
      //    comment above for why an in-place re-render is not possible from
      //    within this tab.
      window.location.reload();
    } catch (e) {
      console.error('Failed to update display name:', e);
      setError('Could not save your name. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Editable display name */}
      <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold mb-2">Display Name</h3>
        <p className="text-sm text-arc-muted mb-4">
          This is the name shown in the app header.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            placeholder="Your name"
            maxLength={80}
            className="flex-1 px-3 py-2 rounded-arc-ctl bg-white/[0.03] ring-1 ring-arc-line text-arc-ink placeholder:text-arc-ink-4 focus:outline-none focus:ring-2 focus:ring-arc-accent-ink disabled:opacity-50"
          />
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="btn-tactile px-4 py-2 text-sm font-semibold rounded-arc-ctl bg-arc-card text-arc-ink ring-1 ring-arc-line hover:bg-arc-paper-2 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Name'}
          </button>
        </div>

        {error && (
          <p className="mt-3 text-sm text-red-600">{error}</p>
        )}
        <p className="mt-3 text-xs text-arc-ink-4">
          Saving updates your name everywhere and briefly refreshes the page.
        </p>
      </div>

      {/* Original Financial Profile Summary */}
      <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold mb-6">Financial Profile Summary</h3>

        {/* Two-column summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Income sources */}
          <div>
            <h4 className="font-medium mb-3 text-arc-muted">Income Sources</h4>
            <div className="space-y-2">
              {[
                ['Primary Salary', 'Active'],
                ['Business Revenue', 'Variable'],
                ['Rental Income', 'Passive'],
                ['Crypto Income', 'Variable'],
                ['Affiliate Income', 'Variable'],
              ].map(([label, status]) => (
                <div key={label} className="flex justify-between">
                  <span>{label}</span>
                  <span className="font-semibold">{status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Health metrics */}
          <div>
            <h4 className="font-medium mb-3 text-arc-muted">Financial Health Metrics</h4>
            <div className="space-y-2">
              {[
                ['Debt-to-Income Ratio', 'Low', 'text-green-600'],
                ['Emergency Fund Status', 'Needs Attention', 'text-red-600'],
                ['Investment Diversification', 'Moderate', 'text-yellow-600'],
                ['FIRE Progress', 'On Track', 'text-blue-600'],
              ].map(([label, value, color]) => (
                <div key={label} className="flex justify-between">
                  <span>{label}</span>
                  <span className={`font-semibold ${color}`}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Habits — generic financial-wellness principles, not
            personalized advice. The previous version included
            "Consider rebalancing crypto-heavy investment portfolio"
            which crossed the advice line. Reframed as habits that any
            student can practice independent of their specific portfolio. */}
        <div className="mt-6 p-4 bg-white/[0.03] rounded-lg">
          <h4 className="font-medium mb-2">Healthy financial habits</h4>
          <ul className="text-sm space-y-1 text-arc-muted list-disc list-inside">
            <li>
              Build emergency fund to 6&nbsp;months of expenses ($
              {emergencyFund.sixMonthGoal.toLocaleString()})
            </li>
            <li>Pay down high-interest debt before adding new risk</li>
            <li>Track and categorize large expenses for budgeting clarity</li>
            <li>Diversify income streams where possible</li>
            <li>Revisit your Risk Profile when life circumstances change</li>
          </ul>
        </div>
      </div>

      {/* Risk Assessment used to render here. It now lives in its own
          top-level sidebar tab (see RiskAssessmentTab) so students find
          it faster and can return to it as a primary surface. */}
    </div>
  );
};

export default ProfileTab;
